import { useRucStore } from "@/domains/ruc";
import { useVehicleStore } from "@/domains/vehicles";
import { computed, type ComputedRef } from "vue";
import { FUEL_PRICE_PER_LITRE, RUC_COST_PER_KM } from "./tripCostConstants";
import type { Trip } from "./types";

export interface TripCostEstimates {
  /** Total driving distance for the trip, in km. */
  tripDistanceKm: number;
  /** Estimated fuel used for the trip, in litres, or undefined if the vehicle's EPA rating hasn't resolved/fetched. */
  fuelUsedLitres: number | undefined;
  /** Estimated fuel cost for the trip, in NZD, or undefined if fuelUsedLitres is undefined. */
  fuelCost: number | undefined;
  /** RUC (Road User Charges) cost for the trip's distance, at today's rate. */
  rucCostAtCurrentRate: number;
  /**
   * RUC cost as display text. Petrol/hybrid vehicles don't pay RUC today -
   * they pay fuel excise duty at the pump instead - so this shows $0.00 now
   * plus the future cost from the planned 2027/28 RUC extension to light
   * petrol/hybrid vehicles, rather than today's charge.
   */
  rucCostText: string;
  /** Estimated CO2-equivalent emissions for the trip, in kg, or undefined if the vehicle's EPA rating hasn't resolved/fetched. */
  co2Kg: number | undefined;
  /** co2Kg as display text, or "unknown" when co2Kg is undefined. */
  co2Text: string;
}

/**
 * Derives fuel/RUC/CO2 cost estimates for a trip from its driving distance
 * and its vehicle's EPA fuel efficiency rating.
 *
 * Shared between TripDetailsPage.vue and TripStatsContent.vue so the
 * units/math reasoning (e.g. km-per-litre needs dividing, not multiplying,
 * to get litres consumed) lives in one place instead of being duplicated.
 */
export function useTripCostEstimates(
  trip: ComputedRef<Trip | null | undefined>,
): ComputedRef<TripCostEstimates> {
  const vehicleStore = useVehicleStore();
  const rucStore = useRucStore();

  return computed(() => {
    const tripDistanceKm =
      (trip.value?.itinerary.totalDrivingDistance ?? 0) / 1000;

    const epaRating = trip.value?.vehicle?.externalId
      ? vehicleStore.getEpaFuelEfficiencyRatingForVehicle(
          trip.value.vehicle.externalId,
        )
      : undefined;

    // ice_combined_kpl is km-per-litre, so litres consumed = distance / kpl.
    const kpl = epaRating?.ice_combined_kpl;
    const fuelUsedLitres = kpl ? tripDistanceKm / kpl : undefined;

    const fuelCost =
      fuelUsedLitres !== undefined
        ? fuelUsedLitres * FUEL_PRICE_PER_LITRE
        : undefined;

    // Prefer the vehicle's own most recently-purchased rate over the flat
    // placeholder constant when one is available - see rucStore.getLatestRateForVehicle.
    const vehicleRucRate1000km = trip.value?.vehicle?.externalId
      ? rucStore.getLatestRateForVehicle(trip.value.vehicle.externalId)
      : undefined;

    const rucCostPerKm =
      vehicleRucRate1000km !== undefined
        ? vehicleRucRate1000km / 1000
        : RUC_COST_PER_KM;

    const rucCostAtCurrentRate = tripDistanceKm * rucCostPerKm;
    const fuelType = trip.value?.vehicle?.fuelType;
    const paysRucToday = fuelType !== "PETROL" && fuelType !== "HYBRID";
    const currentRateText = `$${rucCostAtCurrentRate.toFixed(2)}`;
    const rucCostText = paysRucToday
      ? currentRateText
      : `$0.00 (${currentRateText} from 2027/28 onwards)`;

    // emissions_co2e_g_per_km is grams of CO2-equivalent per km.
    const co2GPerKm = epaRating?.emissions_co2e_g_per_km;
    const co2Kg = co2GPerKm ? (co2GPerKm * tripDistanceKm) / 1000 : undefined;
    const co2Text = co2Kg !== undefined ? `${co2Kg.toFixed(1)} kg CO₂e` : "unknown";

    return {
      tripDistanceKm,
      fuelUsedLitres,
      fuelCost,
      rucCostAtCurrentRate,
      rucCostText,
      co2Kg,
      co2Text,
    };
  });
}
